/** @author PVCOMBANK */
import * as React from "react";
import { Paragraph, TextInput, useTheme } from "react-native-paper";
import { RegisterOptions, useForm } from "react-hook-form";
import { Button } from "react-native-paper";
import { Block } from "@block";
import { useDesktopMediaQuery } from "@media-query";
import { useTranslation } from "react-i18next";
import { spacing } from "@styles/spacing";
import { QueueSpacing, StackSpacing } from "@components/spacing";
import { InputText, InputAutoComplete, InputGridListView, InputTextCurrency } from "app/shared/components/form/input";
import { StyleSheet } from "react-native";
import { ACCOUNT, EBINQUIRY } from "app/shared/services/services-model.type";
import { DataTopUpCreate } from "../../reducer/types";
import { useDispatch, useSelector } from "react-redux";
import {
  handlerTopUpPrinces,
  handlerTopUpProviders,
  handlerTopUpServices,
  getTopUpProvider,
  getTopUpService,
  loadTopUpServiceJson,
} from "../../top-up-utils/top-up.utils";
import { getTopUpMakeFeeSelector, getTopUpResCustomerInfoSelector, getTopUpStoreSelector } from "../../reducer/selectors";
import { ServiceCode } from "app/shared/services/api-path";
import { ValidatePhoneField } from "app/shared/util/validate";
import { FeeDetailModalNew } from "app/shared/components/modal/make-fee/fee-detail.modal";
import BeneModal from "app/shared/components/modal/bene-account-source/select-bene.modal";
import { Constant } from "app/shared/constant/constant";
import { PROFILE_BENE } from "../../../beneficiaries/utils/bene.utils";
import IconsYellowBanking from "app/shared/components/icons/icon-yellow-banking/IconsYellowBanking";
import { setTopUpStore } from "../../reducer/actions";
import FormViewFee from "app/shared/components/form/fee/form-fee";
import { resetListBene } from "app/modules/beneficiaries/reducer/actions";
import { testProps } from "app/shared/util/test-props";
import { ItemCompleteProps } from "app/shared/components/modal/auto-complete/auto-complete";
import { getLimitRemainSelector } from "app/modules/account-management/reducer/selectors";
import { amountConfig } from "app/modules/transfer/transfer-utils/transfer.utils";

export interface FormTopUpProps {
  onSubmit: Function;
  accountDebit: ACCOUNT;
  onCancel: Function;
  requestCreate: Function;
  requestMakeFee: Function;
  showModal: Function;
  closeModal: Function;
}

enum FormEnum {
  serviceType,
  serviceName,
  providerID,
  providerName,
  customerCode,
  amount,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export type FormTopUpData = {
  serviceType: string;
  serviceName: string;
  providerID: string;
  providerName: string;
  customerCode: string;
  amount: string;
};

const Type = {
  PHONE: "100001",
  OTHER: "OTHER",
};

const FormTopUpPhone = (props: FormTopUpProps) => {
  const { onSubmit, requestCreate, onCancel, accountDebit, requestMakeFee, showModal, closeModal } = props;
  const { colors } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isDesktop = useDesktopMediaQuery();

  // Instance Form Method
  const { control, handleSubmit, getValues, setValue, clearErrors, trigger, watch } = useForm<FormTopUpData>({
    defaultValues: {
      serviceType: "",
      serviceName: "",
      providerID: "",
      providerName: "",
      customerCode: "",
      amount: "",
    },
    mode: "onChange",
  });

  const topUpStore = useSelector(getTopUpStoreSelector);
  const responseMakeFee = useSelector(getTopUpMakeFeeSelector);
  const limitRemainStore = useSelector(getLimitRemainSelector);
  const customerInfo = useSelector(getTopUpResCustomerInfoSelector);

  const [serviceArray, setServiceArray] = React.useState<ItemCompleteProps[]>([]);
  const [providerArray, setProviderArray] = React.useState<ItemCompleteProps[]>([]);
  const [priceArray, setPriceArray] = React.useState<ItemCompleteProps[]>([]);
  const [validateAmount, setValidateAmount] = React.useState({ maxAmount: 0, errorMaxDebit: "" });
  const [topUpType, setTopUpType] = React.useState<string>("");
  const [dataFee, setDataFee] = React.useState<EBINQUIRY | undefined>(undefined);
  const [isBene, setIsBene] = React.useState<boolean>(false);
  const [accountBene, setAccountBene] = React.useState<PROFILE_BENE | undefined>();

  // Styles Component
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        fieldFromText: {
          color: colors.text,
          width: 200,
          fontWeight: "700",
          fontSize: 16,
        },
        buttonContainer: { width: 128, alignSelf: "center", backgroundColor: colors.notification },
        acceptButtonContainer: { width: 128, alignSelf: "center" },
      }),
    [colors.text, colors.notification],
  );

  // Tạo validate Schema
  const validateSchemeForm: ValidDataSchemeFormData = {
    serviceType: {
      required: {
        value: true,
        message: t("TOPUP_VUI_LONG_CHON_DICH_VU"),
      },
    },
    serviceName: {
      required: {
        value: false,
        message: "",
      },
    },
    providerID: {
      required: {
        value: topUpType === Type.OTHER ? true : false,
        message: t("TOPUP_VUI_LONG_CHON_NHA_CUNG_CAP"),
      },
    },
    providerName: {
      required: {
        value: false,
        message: "",
      },
    },
    customerCode: {
      required: {
        value: true,
        message: topUpType === Type.PHONE ? t("TOPUP_VUI_LONG_NHAP_SO_DIEN_THOAI") : t("TOPUP_VUI_LONG_NHAP_SO_DIEN_THOAI_SO_TAI_KHOAN"),
      },
      validate: {
        phone: (value) => {
          if (topUpType === Type.PHONE && !ValidatePhoneField(value)) {
            return t("TOPUP_SO_DIEN_THOAI_KHONG_HOP_LE").toString();
          }
          return true;
        },
      },
    },
    amount: {
      required: {
        value: true,
        message: topUpType === Type.PHONE ? t("TOPUP_VUI_LONG_CHON_MENH_GIA") : t("TOPUP_VUI_LONG_NHAP_SO_TIEN"),
      },
      validate: {
        amountMin: (value) => {
          if (Number(value) < 10000) {
            return t("COMMON_SO_TIEN_KHONG_DUOC_DUOI_10000D").toString();
          }
          return true;
        },
        amountMax: (value) => {
          if (validateAmount?.maxAmount && Number(value) > validateAmount?.maxAmount) {
            return validateAmount?.errorMaxDebit;
          }
          return true;
        },
      },
    },
  };

  const handleRequestCreate = (value: FormTopUpData): void => {
    const dataUpdate: DataTopUpCreate = {
      ACCOUNT_DEBIT: accountDebit,
      AMOUNT: value.amount,
      SERVICE: {
        value: value.serviceType,
        label: value.serviceName,
      },
      SERVICE_CODE: value.serviceType === Type.PHONE ? ServiceCode.PAYMENT_TOP_UP_PHONE : ServiceCode.PAYMENT_TOP_UP_OTHER,
      INQUIRY_CODE: value.customerCode,
      BENE:
        isBene &&
        ((accountBene?.CUSTOMER_CODE?.length && accountBene?.CUSTOMER_CODE === value.customerCode) ||
          (accountBene?.PHONE?.length && accountBene?.PHONE === value.customerCode))
          ? accountBene
          : undefined,
    };
    if (value.serviceType !== Type.PHONE) {
      dataUpdate.PROVIDER = {
        value: value.providerID,
        label: value.providerName,
      };
    }
    requestCreate(dataUpdate);
  };

  const handleRequestMakeFee = (value: FormTopUpData): void => {
    const dataUpdate: DataTopUpCreate = {
      ACCOUNT_DEBIT: accountDebit,
      AMOUNT: value.amount,
      SERVICE: {
        value: value.serviceType,
        label: value.serviceName,
      },
      SERVICE_CODE: value.serviceType === Type.PHONE ? ServiceCode.PAYMENT_TOP_UP_PHONE : ServiceCode.PAYMENT_TOP_UP_OTHER,
      INQUIRY_CODE: value.customerCode,
    };
    if (value.serviceType !== Type.PHONE) {
      dataUpdate.PROVIDER = {
        value: value.providerID,
        label: value.providerName,
      };
    }
    if (dataFee) setDataFee(undefined);
    requestMakeFee(dataUpdate);
  };

  const handlerSelectBene = (temp: PROFILE_BENE) => {
    closeModal();
    setIsBene(true);
    setAccountBene(temp);
    clearErrors("serviceType");
    clearErrors("customerCode");
    clearErrors("providerID");
    setValue("serviceType", temp.SERVICE || "");
    setValue("customerCode", temp.PHONE || temp.CUSTOMER_CODE || "");
    if (temp.SERVICE !== Type.PHONE) {
      setValue("providerID", temp.PROVIDER || "");
      if (temp.PROVIDER?.length) {
        const listProvider = handlerTopUpProviders(temp.SERVICE);
        setTimeout(() => {
          setProviderArray(listProvider);
        }, 0);
      }
    } else {
      setValue("amount", "");
      clearErrors("amount");
    }
  };

  /// MARK: - Action Handler
  const onPressCreate = () => {
    onSubmit();
    handleSubmit(handleRequestCreate).call(() => {});
  };

  const onPressFeeDetail = async () => {
    onSubmit();
    handleSubmit(handleRequestMakeFee).call(() => {});
  };

  const onPressBeneAndAccount = () => {
    dispatch(resetListBene());
    showModal(
      <BeneModal
        title={t("BENE_CHON_SDT_TK_DV")}
        templateType={Constant.TEMPLATE_TYPE.TOP_UP_TEMPLATE}
        onPressAcceptBene={handlerSelectBene}
      />,
    );
  };

  const onClickProvider = () => {
    trigger("serviceType");
  };

  const checkSelectProvider = () => {
    if (getValues("serviceType").length) return false;
    return true;
  };

  /// MARK: - Bussiness Logic HERE
  const onSelectedService = (service?: ItemCompleteProps) => {
    console.log("onSelectedService: ", service);
    if (accountBene?.SERVICE?.length && accountBene?.SERVICE !== service?.value) {
      setAccountBene(undefined);
    }
    setValue("serviceName", service?.label || "");
    if (getValues("customerCode").length) {
      setValue("customerCode", "");
      clearErrors("customerCode");
    }
    if (providerArray.length) setProviderArray([]);
    if (service?.value !== Type.PHONE && getValues("providerID").length) {
      setValue("providerID", "");
      setValue("providerName", "");
      clearErrors("providerID");
    }
    if (customerInfo?.INQUIRY_CODE?.length) {
      dispatch(setTopUpStore({ ...topUpStore, customerInfo: undefined }));
    }
    if (service?.value !== Type.PHONE) {
      const listProvider = handlerTopUpProviders(service?.value);
      setProviderArray(listProvider);
    } else {
      setValue("amount", "");
      clearErrors("amount");
    }
  };

  const onSelectedProvider = (provider?: ItemCompleteProps) => {
    setValue("providerName", provider?.label || "");
    if (getValues("customerCode").length) {
      setValue("customerCode", "");
      clearErrors("customerCode");
    }
  };

  const onSelectedPrice = (price?: ItemCompleteProps) => {
    setValue("amount", price?.value || "");
    trigger("amount");
  };

  // set thông tin topUp
  const onBlurTopUpCustomer = () => {
    trigger("customerCode");
    const customerCode = getValues("customerCode") || "";
    if (
      customerCode.length &&
      ((accountBene?.CUSTOMER_CODE?.length && accountBene?.CUSTOMER_CODE === customerCode) ||
        (accountBene?.PHONE?.length && accountBene?.PHONE === customerCode))
    ) {
      setIsBene(true);
    } else {
      setIsBene(false);
    }
  };
  ///
  const chooseAccountBene = (
    <TextInput.Icon name={() => <IconsYellowBanking name={"danhsachthuhuong"} size={28} />} size={28} onPress={onPressBeneAndAccount} />
  );
  /// MARK: - Child Component
  const FieldForm = (
    <Block>
      <StackSpacing size="medium" />
      <Paragraph style={styles.fieldFromText}>{t("TRANFER_TAI_KHOAN_NHAN")}</Paragraph>
      <StackSpacing size="medium" />
    </Block>
  );

  const FormInput = (
    <Block flex={1} bg={colors.backgroundForm} br={12} p={spacing.large}>
      <InputAutoComplete
        rules={validateSchemeForm.serviceType}
        name="serviceType"
        options={serviceArray}
        control={control}
        title={t("TOPUP_DICH_VU")}
        label={t("TOPUP_CHON_DICH_VU")}
        onDismiss={onSelectedService}
        showSearch={true}
        showIcon={true}
      />
      <InputAutoComplete
        rules={validateSchemeForm.providerID}
        name="providerID"
        options={providerArray}
        control={control}
        title={t("TOPUP_NHA_CUNG_CAP")}
        label={t("TOPUP_CHON_NHA_CUNG_CAP")}
        onClick={onClickProvider}
        checkSelect={checkSelectProvider()}
        onDismiss={onSelectedProvider}
        isShow={topUpType === Type.OTHER}
        showSearch={true}
      />
      <InputText
        name="customerCode"
        control={control}
        rules={validateSchemeForm.customerCode}
        inputProps={{ right: chooseAccountBene }}
        label={topUpType === Type.PHONE ? t("TOPUP_SO_DIEN_THOAI") : t("TOPUP_SO_DIEN_THOAI_TAI_KHOAN_DICH_VU")}
        onBlur={onBlurTopUpCustomer}
      />
      <InputTextCurrency
        name="amount"
        keyboardType={"numeric"}
        control={control}
        rules={validateSchemeForm.amount}
        inputProps={{ editable: topUpType === Type.PHONE ? false : true }}
        label={topUpType === Type.PHONE ? t("TOPUP_MENH_GIA") : t("TOPUP_SO_TIEN")}
        rightCurrency={t("COMMON_VND")}
      />
      <StackSpacing size="medium" />
      {topUpType === Type.PHONE ? <InputGridListView onSelected={onSelectedPrice} values={priceArray} /> : <Block />}
      <FormViewFee onClick={onPressFeeDetail} />
    </Block>
  );

  const ButtonForm = (
    <Block row middle pv={spacing.huge}>
      <Block w={isDesktop ? 200 : 0} />
      <Button onPress={() => onCancel()} mode="contained" style={styles.buttonContainer}>
        {t("COMMON_HUY")}
      </Button>
      <QueueSpacing size="large" />
      <Button mode="contained" style={styles.acceptButtonContainer} onPress={onPressCreate} {...testProps("nextBtn")}>
        {t("COMMON_TIEP_TUC")}
      </Button>
    </Block>
  );

  React.useEffect(() => {
    loadTopUpServiceJson().then(() => {
      const listService = handlerTopUpServices();
      const listPrice = handlerTopUpPrinces();
      setServiceArray(listService);
      setPriceArray(listPrice);
    });
  }, []);

  React.useEffect(() => {
    // lấy hạn mức gd ngày/ số dư khả dụng
    const { maxAmount, errorMaxDebit } = amountConfig(accountDebit, limitRemainStore);
    setValidateAmount({ maxAmount, errorMaxDebit });
  }, [limitRemainStore, accountDebit]);

  React.useEffect(() => {
    if (validateAmount.errorMaxDebit?.length && getValues("amount").length) trigger("amount");
  }, [getValues, trigger, validateAmount]);

  React.useEffect(() => {
    if (accountBene?.SERVICE?.length && accountBene?.SERVICE === getValues("serviceType")) {
      const serviceName = getTopUpService(accountBene?.SERVICE)?.NAME || "";
      setValue("serviceName", serviceName);
    }
  }, [accountBene, setValue, getValues]);

  React.useEffect(() => {
    if (
      accountBene?.SERVICE?.length &&
      accountBene?.SERVICE !== Type.PHONE &&
      accountBene?.SERVICE === getValues("serviceType") &&
      accountBene?.PROVIDER?.length &&
      providerArray
    ) {
      const providerName = getTopUpProvider(accountBene?.SERVICE, accountBene?.PROVIDER)?.NAME || "";
      setValue("providerID", accountBene.PROVIDER);
      setValue("providerName", providerName);
    }
  }, [accountBene, getValues, providerArray, setValue]);

  React.useEffect(() => {
    if (responseMakeFee?.TRANSFER) {
      setDataFee(responseMakeFee.TRANSFER);
    }
  }, [responseMakeFee]);

  // show Phí
  React.useEffect(() => {
    if (dataFee) {
      showModal(<FeeDetailModalNew data={dataFee} feeType={Constant.TRANSFER.TRANSFER_FEE_TYPE.O} />);
      setDataFee(undefined);
    }
  }, [accountDebit, getValues, dataFee, showModal]);

  React.useEffect(() => {
    const subscription = watch(({ serviceType }) => {
      if (serviceType === Type.PHONE) {
        setTimeout(() => {
          setTopUpType(Type.PHONE);
        }, 0);
      } else if (serviceType !== Type.PHONE && serviceType !== "") {
        setTimeout(() => {
          setTopUpType(Type.OTHER);
        }, 0);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <>
      <Block row={isDesktop ? true : false} pt={spacing.medium}>
        {FieldForm}
        {FormInput}
      </Block>
      {ButtonForm}
    </>
  );
};

export default FormTopUpPhone;
